/**
 * Saved Card Messages — cart page & drawer viewer/clearer.
 * Shares the localStorage key and cart attribute with Write with Us, and owns
 * post-checkout clearing.
 */
(function () {
  'use strict';

  const CLIPBOARD_KEY = 'qcMessageClipboard';
  const AWAITING_CHECKOUT_KEY = 'qcClipboardAwaitingCheckout';
  const ATTR_KEY = 'Saved Card Messages';

  function escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function loadClipboard() {
    try {
      const arr = JSON.parse(localStorage.getItem(CLIPBOARD_KEY) || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function saveClipboard(arr) {
    try {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(arr));
    } catch (e) {
      /* ignore quota errors */
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('qc:clipboard-updated', { bubbles: true })
    );
  }

  function buildAttributeValue(arr) {
    return arr
      .map(
        (item) =>
          `${item.card || ''}|||${item.text || ''}|||${item.image || ''}|||${item.url || ''}`
      )
      .join('@@@');
  }

  function parseAttribute(str) {
    if (!str || typeof str !== 'string') return [];
    return str
      .split('@@@')
      .filter(Boolean)
      .map((part) => {
        const parts = part.split('|||');
        return {
          card: parts[0] || '',
          text: parts[1] || (parts.length === 1 ? part : ''),
          image: parts[2] || '',
          url: parts[3] || '',
          savedAt: 0,
        };
      })
      .filter((item) => item.text);
  }

  function cartJsonUrl() {
    return window.theme?.routes?.cart_url
      ? `${window.theme.routes.cart_url}.js`
      : '/cart.js';
  }

  async function syncCartAttribute(arr) {
    try {
      await fetch(
        `${window.theme?.routes?.cart_update_url || '/cart/update'}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [ATTR_KEY]: arr.length ? buildAttributeValue(arr) : '',
            },
          }),
        }
      );
    } catch (e) {
      /* non-fatal */
    }
  }

  function markAwaitingCheckout() {
    if (!loadClipboard().length) return;
    try {
      localStorage.setItem(AWAITING_CHECKOUT_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  }

  function isCheckoutTrigger(target) {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest(
        '[name="checkout"], .checkout-button, .additional-checkout-buttons, .cart__dynamic-checkout-buttons'
      )
    );
  }

  // Capture phase, so the flag is written before the form navigates away.
  if (!window.__qcClipboardCheckoutInit) {
    window.__qcClipboardCheckoutInit = true;

    document.addEventListener(
      'click',
      (event) => {
        if (isCheckoutTrigger(event.target)) markAwaitingCheckout();
      },
      true
    );

    // Checkout was clicked and the live cart is now empty, so the order went
    // through. localStorage rather than sessionStorage: checkout may open a tab.
    (async function clearClipboardAfterCheckout() {
      if (localStorage.getItem(AWAITING_CHECKOUT_KEY) !== '1') return;
      if (!loadClipboard().length) {
        localStorage.removeItem(AWAITING_CHECKOUT_KEY);
        return;
      }
      try {
        const res = await fetch(cartJsonUrl());
        if (!res.ok) return;
        const cart = await res.json();
        if (cart.item_count === 0) {
          localStorage.removeItem(AWAITING_CHECKOUT_KEY);
          saveClipboard([]);
          await syncCartAttribute([]);
        }
      } catch (e) {
        /* non-fatal */
      }
    })();
  }

  if (!customElements.get('saved-card-messages')) {
    class SavedCardMessages extends HTMLElement {
      constructor() {
        super();
        this.open = false;
        this._onClick = this._onClick.bind(this);
        this._onClipboardUpdated = this._onClipboardUpdated.bind(this);
        this._onPanelScroll = this._onPanelScroll.bind(this);
      }

      connectedCallback() {
        this.hydrateFromAttributeIfNeeded();
        this.render();
        this.addEventListener('click', this._onClick);
        document.documentElement.addEventListener(
          'qc:clipboard-updated',
          this._onClipboardUpdated
        );
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this._unbindPanelScroll();
        document.documentElement.removeEventListener(
          'qc:clipboard-updated',
          this._onClipboardUpdated
        );
      }

      hydrateFromAttributeIfNeeded() {
        const existing = loadClipboard();
        if (existing.length) return;

        let raw = '';
        const jsonEl = this.querySelector('script[type="application/json"][data-qc-cart-messages]');
        if (jsonEl) {
          try {
            raw = JSON.parse(jsonEl.textContent) || '';
          } catch (e) {
            raw = '';
          }
        }

        const fromAttr = parseAttribute(raw);
        if (fromAttr.length) {
          try {
            localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(fromAttr));
          } catch (e) {
            /* ignore */
          }
        }
      }

      _onClipboardUpdated() {
        this.render();
      }

      _onPanelScroll() {
        this._updateScrollHint();
      }

      _unbindPanelScroll() {
        const panel = this.querySelector('[data-qc-scroll]');
        if (panel) panel.removeEventListener('scroll', this._onPanelScroll);
      }

      _bindPanelScroll() {
        const panel = this.querySelector('[data-qc-scroll]');
        if (!panel) return;
        panel.addEventListener('scroll', this._onPanelScroll, { passive: true });
      }

      _updateScrollHint() {
        const wrap = this.querySelector('.qc-clipboard-panel-wrap');
        const panel = this.querySelector('[data-qc-scroll]');
        if (!wrap || !panel || !this.open) {
          wrap?.classList.remove('has-more');
          return;
        }
        const canScrollMore =
          panel.scrollHeight - panel.scrollTop - panel.clientHeight > 6;
        wrap.classList.toggle('has-more', canScrollMore);
      }

      _onClick(event) {
        if (event.target.closest('[data-qc-toggle-clipboard]')) {
          event.preventDefault();
          this.open = !this.open;

          const accordion = this.querySelector('.qc-saved-accordion');
          const toggle = this.querySelector('[data-qc-toggle-clipboard]');
          if (accordion && this.querySelector('[data-qc-scroll]')) {
            accordion.classList.toggle('is-open', this.open);
            toggle?.setAttribute('aria-expanded', String(this.open));
            if (!this.open) {
              this.querySelector('.qc-clipboard-panel-wrap')?.classList.remove(
                'has-more'
              );
            } else {
              requestAnimationFrame(() => this._updateScrollHint());
              setTimeout(() => this._updateScrollHint(), 320);
            }
            return;
          }

          this.render();
          return;
        }

        const removeBtn = event.target.closest('[data-qc-remove-clipboard]');
        if (removeBtn) {
          event.preventDefault();
          const idx = Number(removeBtn.getAttribute('data-qc-remove-clipboard'));
          const arr = loadClipboard();
          if (!Number.isNaN(idx) && idx >= 0 && idx < arr.length) {
            arr.splice(idx, 1);
            saveClipboard(arr);
            syncCartAttribute(arr);
          }
          if (!arr.length) this.open = false;
          this.render();
        }
      }

      render() {
        this._unbindPanelScroll();
        const arr = loadClipboard();

        if (!arr.length) {
          this.hidden = true;
          this.innerHTML = '';
          return;
        }

        this.hidden = false;

        const items = arr
          .map(
            (item, i) => `
          <div class="qc-clipboard-item">
            <div class="qc-clipboard-item-main">
              ${
                item.card
                  ? `<div class="qc-clipboard-item-card">${escapeHtml(item.card)}</div>`
                  : ''
              }
              <div class="qc-clipboard-item-text">${escapeHtml(item.text)}</div>
            </div>
            <button type="button" class="qc-clipboard-remove" data-qc-remove-clipboard="${i}">Clear</button>
          </div>`
          )
          .join('');

        this.innerHTML = `
          <div class="qc-saved-accordion${this.open ? ' is-open' : ''}">
            <button type="button" class="qc-clipboard-bar" data-qc-toggle-clipboard aria-expanded="${this.open}">
              <span>Saved messages</span>
              <span class="qc-clipboard-bar-end">
                <span class="count">${arr.length}</span>
                <span class="qc-saved-chevron" aria-hidden="true"></span>
              </span>
            </button>
            <div class="qc-clipboard-panel-wrap">
              <div class="qc-clipboard-panel" data-qc-scroll>
                ${items}
              </div>
              <div class="qc-clipboard-fade" aria-hidden="true"></div>
            </div>
          </div>
        `;

        this._bindPanelScroll();
        requestAnimationFrame(() => this._updateScrollHint());
      }
    }

    customElements.define('saved-card-messages', SavedCardMessages);
  }
})();
